/**
 * Parse a plain-markdown CV-screening reply from the AI chat.
 *
 * The backend prompts Claude to format screening results with strict h2
 * section headers:
 *
 *   ## CV Screening Results — Senior Engineer
 *
 *   ## ✅ SHORTLISTED CANDIDATES
 *   **1. Jane Doe** — strong React background
 *   - ✅ 5+ years experience
 *   → Recommended for interview
 *
 *   ## ❌ REJECTED CANDIDATES
 *   **2. John Smith**
 *   - ❌ No relevant experience
 *
 *   ## Summary
 *   - Total reviewed: 2
 *
 * This is 100% content-based — it never depends on any DB flag. It returns:
 *   { isScreening, position, shortlisted: [name, ...] }
 *
 * `isScreening` is true when the text contains a SHORTLISTED section or a
 * "Recommended for interview" line. `shortlisted` holds ONLY the candidate
 * names found inside the ✅ SHORTLISTED CANDIDATES section.
 */

// Pull the position from the "## CV Screening Results — <Position>" heading.
function extractPosition(text) {
  const m = text.match(/##\s*CV Screening Results\s*[—–-]\s*(.+)/i);
  return m ? m[1].trim() : '';
}

// Candidate lines look like:  **1. Jane Doe** — highlights
// We require the mandatory "**N. " prefix (bold number + dot + space) and
// capture a name-shaped string only. This deliberately ignores any other bold
// text such as "**51 years old**" or "**FAILS age requirement**" — those have
// no leading "N." and are skipped. The trailing " — highlights" lives outside
// the bold, so it is never captured.
function extractNamesFromSection(section) {
  const names = [];
  const re = /^\s*\*\*\s*\d+\.\s+([A-Za-z][A-Za-z .'-]*?)\s*\*\*/gm;
  let m;
  while ((m = re.exec(section)) !== null) {
    const name = m[1].trim();
    if (name) names.push(name);
  }
  return names;
}

export function parseScreeningMarkdown(text) {
  const empty = { isScreening: false, position: '', shortlisted: [] };
  if (!text || typeof text !== 'string') return empty;

  const hasShortlistHeader = /SHORTLISTED/i.test(text);
  const hasRecommended = /Recommended for interview/i.test(text);
  if (!hasShortlistHeader && !hasRecommended) return empty;

  // Isolate the SHORTLISTED section: from its h2 heading up to the NEXT h2
  // heading (REJECTED CANDIDATES / Summary) or the end of the message. The
  // (?!#) lookahead matches a level-2 "##" heading but never a level-3 "###".
  let shortlisted = [];
  const headerMatch = text.match(/^##(?!#)\s*[^\n]*SHORTLISTED[^\n]*$/im);
  if (headerMatch) {
    const start = headerMatch.index + headerMatch[0].length;
    const rest = text.slice(start);
    const nextHeading = rest.search(/\n##(?!#)\s/);
    const section = nextHeading === -1 ? rest : rest.slice(0, nextHeading);
    shortlisted = extractNamesFromSection(section);
  }

  return {
    isScreening: true,
    position: extractPosition(text),
    shortlisted,
  };
}
